// src/services/SecurityService.mjs
//
// Account security: sessions/devices, password change, two-factor, and the
// sign-in methods linked to the account.
//
// Most of this runs in the Firebase client SDK rather than against our backend,
// because that's where the credentials actually live. The backend is told
// afterwards (`reportSecurityEvent`) so it can write the audit entry and send the
// alert email — the two things the SDK can't do.
import {
    EmailAuthProvider,
    GoogleAuthProvider,
    TotpMultiFactorGenerator,
    TotpSecret,
    getMultiFactorResolver,
    linkWithPopup,
    multiFactor,
    reauthenticateWithCredential,
    reauthenticateWithPopup,
    unlink,
    updatePassword,
} from 'firebase/auth';
import apiClient from '../config/axiosConfig.js';
import { auth, googleProvider } from '../config/firebaseConfig.js';
import { resetSessionId } from '../config/sessionId.js';

// ─────────────────────────────────────────────────────────────────────────────
// Sessions & devices
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Where this account is signed in.
 *
 * Also returns `revocationCheckEnabled`, which the UI must respect: with it off,
 * signing out everywhere stops NEW tokens being issued but leaves ID tokens
 * already in a browser valid until they expire. Promising more than that would be
 * a lie about a security control.
 */
export async function listSessions() {
    const response = await apiClient.get('/api/auth/sessions');
    return response.data; // { sessions, revocationCheckEnabled }
}

export async function signOutEverywhere() {
    const response = await apiClient.post('/api/auth/sessions/sign-out-everywhere', null, {
        skipGlobalErrorToast: true,
    });
    // The old device row is now revoked; keeping its id would resurrect it on the
    // next sign-in instead of registering a fresh device.
    resetSessionId();
    return response.data;
}

export async function forgetSession(sessionId) {
    const response = await apiClient.delete(`/api/auth/sessions/${encodeURIComponent(sessionId)}`);
    return response.data;
}

/** Tell the backend about a change the Firebase SDK made, for audit + alerting. */
export async function reportSecurityEvent(type, detail) {
    try {
        await apiClient.post('/api/auth/security-event', { type, detail }, { skipGlobalErrorToast: true });
    } catch (err) {
        // The security change itself already succeeded in Firebase. Failing to
        // log it must not be reported to the user as the change having failed.
        console.warn('Could not report security event:', type, err);
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Re-authentication
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Firebase requires a recent login before sensitive changes (password, MFA,
 * unlinking). Re-authenticates using whichever provider the account actually has,
 * so the caller doesn't have to care which.
 */
export async function reauthenticate(currentPassword) {
    const user = auth.currentUser;
    if (!user) throw new Error('Not signed in.');

    const hasPassword = user.providerData.some(p => p.providerId === 'password');

    if (hasPassword) {
        if (!currentPassword) {
            const err = new Error('Password required');
            err.code = 'qdex/password-required';
            throw err;
        }
        const credential = EmailAuthProvider.credential(user.email, currentPassword);
        return reauthenticateWithCredential(user, credential);
    }

    return reauthenticateWithPopup(user, googleProvider);
}

// ─────────────────────────────────────────────────────────────────────────────
// Password
// ─────────────────────────────────────────────────────────────────────────────

export async function changePassword(currentPassword, newPassword) {
    const user = auth.currentUser;
    if (!user) throw new Error('Not signed in.');

    await reauthenticate(currentPassword);
    await updatePassword(user, newPassword);
    await reportSecurityEvent('password_changed');
}

// ─────────────────────────────────────────────────────────────────────────────
// Sign-in methods (S4)
// ─────────────────────────────────────────────────────────────────────────────

const PROVIDER_LABELS = {
    password: 'Email & password',
    'google.com': 'Google',
};

export function listLinkedProviders() {
    const user = auth.currentUser;
    if (!user) return [];
    return user.providerData.map(p => ({
        providerId: p.providerId,
        label: PROVIDER_LABELS[p.providerId] ?? p.providerId,
        email: p.email,
    }));
}

export async function linkGoogle() {
    const user = auth.currentUser;
    if (!user) throw new Error('Not signed in.');
    await linkWithPopup(user, new GoogleAuthProvider());
    await reportSecurityEvent('provider_linked', 'google.com');
}

/**
 * Remove a sign-in method.
 *
 * Refuses the last one — an account with no way to sign in is unrecoverable, and
 * Firebase will happily let you create one.
 */
export async function unlinkProvider(providerId) {
    const user = auth.currentUser;
    if (!user) throw new Error('Not signed in.');

    if (user.providerData.length <= 1) {
        const err = new Error('This is the only way to sign in to your account.');
        err.code = 'qdex/last-provider';
        throw err;
    }

    await unlink(user, providerId);
    await reportSecurityEvent('provider_unlinked', providerId);
}

// ─────────────────────────────────────────────────────────────────────────────
// Two-factor (S2)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * TOTP multi-factor needs the Firebase project to be on **Identity Platform**
 * (the paid upgrade of Firebase Auth). On a project that isn't, enrolment throws
 * rather than degrading, so the UI has to be able to say WHY instead of showing a
 * generic failure. These are the codes that mean "the project isn't set up for
 * this", as opposed to "you typed the wrong code".
 */
const MFA_UNAVAILABLE_CODES = new Set([
    'auth/operation-not-allowed',
    'auth/unsupported-first-factor',
    'auth/admin-restricted-operation',
]);

export function isMfaUnavailableError(err) {
    return MFA_UNAVAILABLE_CODES.has(err?.code);
}

export function listEnrolledFactors() {
    const user = auth.currentUser;
    if (!user) return [];
    return multiFactor(user).enrolledFactors.map(f => ({
        uid: f.uid,
        displayName: f.displayName || 'Authenticator app',
        enrolledAt: f.enrollmentTime,
    }));
}

/**
 * Begin TOTP enrolment: returns the shared secret plus the otpauth:// URI a
 * QR code is rendered from. Nothing is enrolled until `finishTotpEnrollment`
 * confirms the user can produce a code from it.
 */
export async function startTotpEnrollment() {
    const user = auth.currentUser;
    if (!user) throw new Error('Not signed in.');

    const session = await multiFactor(user).getSession();
    const secret = await TotpMultiFactorGenerator.generateSecret(session);

    return {
        secret,
        // Shown as a QR code, with the raw key as the manual fallback.
        uri: secret.generateQrCodeUrl(user.email ?? 'QDex', 'QDex'),
        sharedSecretKey: secret.secretKey,
    };
}

export async function finishTotpEnrollment(secret, verificationCode, displayName = 'Authenticator app') {
    const user = auth.currentUser;
    if (!user) throw new Error('Not signed in.');

    const assertion = TotpMultiFactorGenerator.assertionForEnrollment(secret, verificationCode);
    await multiFactor(user).enroll(assertion, displayName);
    await reportSecurityEvent('mfa_enabled');
}

export async function unenrollFactor(factorUid, currentPassword) {
    const user = auth.currentUser;
    if (!user) throw new Error('Not signed in.');

    await reauthenticate(currentPassword);
    await multiFactor(user).unenroll(factorUid);
    await reportSecurityEvent('mfa_disabled');
}

/**
 * Complete a sign-in that was interrupted by the second-factor challenge.
 *
 * `signInWithEmailAndPassword` / `signInWithPopup` throw `auth/multi-factor-auth-required`
 * for an enrolled account; the login form catches that, collects a code, and calls
 * this with the original error.
 */
export async function resolveMfaSignIn(mfaError, verificationCode) {
    const resolver = getMultiFactorResolver(auth, mfaError);
    const hint = resolver.hints[0];
    const assertion = TotpMultiFactorGenerator.assertionForSignIn(hint.uid, verificationCode);
    return resolver.resolveSignIn(assertion);
}

export { TotpSecret };
